import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Youtube, Globe, Music2, Mail, MapPin, Pause, Play, Share2, Star } from "lucide-react";
import { API, mediaUrl } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { Equalizer } from "@/components/Equalizer";

const socials = [["instagram", Instagram], ["spotify", Music2], ["youtube", Youtube], ["website", Globe]];

function TrackItem({ t }) {
  const { track, playing, playSingle, toggle } = usePlayer();
  const [open, setOpen] = useState(false);
  const active = track?.id === t.id;
  return (
    <div data-testid={`presskit-track-${t.id}`} className="border-b border-white/5 py-4">
      <div className="flex items-center gap-4">
        <button data-testid={`presskit-play-${t.id}`} onClick={() => (active ? toggle() : playSingle(t))} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#FF4500] text-white">
          {active && playing ? <Pause className="h-4 w-4 fill-white" /> : <Play className="ml-0.5 h-4 w-4 fill-white" />}
        </button>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 truncate font-heading font-bold text-white">{t.title} {active && playing && <Equalizer />}</p>
          <p className="font-mono text-[11px] text-slate-500">{t.plays} reproducciones · {t.likes} me gusta {t.featured && <span className="text-[#FF7A45]">· DESTACADA</span>}</p>
        </div>
        {t.lyrics && <button onClick={() => setOpen(!open)} className="text-xs text-slate-400 hover:text-white">{open ? "Ocultar letra" : "Ver letra"}</button>}
      </div>
      {open && <pre className="mt-4 whitespace-pre-wrap pl-[60px] font-body text-sm leading-relaxed text-slate-400">{t.lyrics}</pre>}
    </div>
  );
}

export default function PressKit() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => { axios.get(`${API}/artists/${id}`).then((r) => setA(r.data)).catch(() => setMissing(true)); }, [id]);

  if (missing) return <p className="px-8 py-32 text-center text-slate-400">Este artista no existe.</p>;
  if (!a) return <div className="h-[60vh]" />;
  const pk = a.presskit || {};
  const name = pk.artist_name || a.display_name || "Artista";
  const cover = a.photos?.[0];
  const share = async () => {
    await navigator.clipboard?.writeText(window.location.href).catch(() => {});
    toast.success("Enlace del Press Kit copiado");
  };

  return (
    <div data-testid="presskit-page">
      <section className={`relative flex items-end overflow-hidden ${cover ? "min-h-[55vh]" : "min-h-[32vh]"}`}>
        {cover ? <img src={mediaUrl(cover)} alt={name} className="absolute inset-0 h-full w-full object-cover" /> : <div className="absolute inset-0 bg-gradient-to-br from-[#2a1208] to-[#0A0A0E]" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0E] via-[#0A0A0E]/60 to-transparent" />
        <div className="relative mx-auto w-full max-w-[1400px] px-4 pb-12 md:px-8">
          <p className="font-mono text-xs tracking-widest text-amber-500/90">PRESS KIT</p>
          <h1 data-testid="presskit-name" className="mt-3 flex flex-wrap items-center gap-4 font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-7xl">{name}{a.is_pro && <span data-testid="presskit-pro-badge" className="rounded-full bg-[#FF4500] px-3 py-1 font-mono text-xs tracking-widest text-white">PRO</span>}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-slate-300">
            {pk.location && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{pk.location}</span>}
            {socials.filter(([k]) => pk[k]).map(([k, Icon]) => (
              <a key={k} href={pk[k]} target="_blank" rel="noreferrer" data-testid={`presskit-social-${k}`} className="flex items-center gap-1.5 hover:text-white"><Icon className="h-4 w-4" />{k}</a>
            ))}
            <button data-testid="presskit-share-btn" onClick={share} className="flex items-center gap-1.5 rounded-full border border-white/20 px-3 py-1 hover:bg-white/10"><Share2 className="h-3.5 w-3.5" />Compartir</button>
          </div>
        </div>
      </section>
      <div className="mx-auto grid max-w-[1400px] gap-16 px-4 py-16 md:px-8 lg:grid-cols-[3fr_2fr]">
        <div>
          <h2 className="font-heading text-2xl font-bold text-white">Canciones</h2>
          <div className="mt-4">{a.tracks.length ? a.tracks.map((t) => <TrackItem key={t.id} t={t} />) : <p className="text-slate-500">Aún no hay canciones.</p>}</div>
          {a.photos?.length > 1 && (
            <>
              <h2 className="mt-14 font-heading text-2xl font-bold text-white">Fotos de prensa</h2>
              <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3">
                {a.photos.map((p) => <a key={p} href={mediaUrl(p)} target="_blank" rel="noreferrer"><img src={mediaUrl(p)} alt="" className="aspect-square w-full rounded-lg object-cover" /></a>)}
              </div>
            </>
          )}
        </div>
        <aside>
          <h2 className="font-heading text-2xl font-bold text-white">Biografía</h2>
          <p data-testid="presskit-bio" className="mt-4 whitespace-pre-line leading-relaxed text-slate-300">{pk.bio || "Este artista todavía no escribió su biografía."}</p>
          {pk.contact && <p className="mt-8 flex items-center gap-2 text-sm text-slate-300"><Mail className="h-4 w-4 text-[#FF4500]" />{pk.contact}</p>}
          {a.tracks.some((t) => t.radio_status === "approved") && <p className="mt-8 flex items-center gap-2 font-mono text-xs text-emerald-400"><Star className="h-4 w-4" />SUENA EN RARA PERLA RADIO</p>}
        </aside>
      </div>
    </div>
  );
}
